import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import i18n, {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  ensureLanguageResources,
  isSupportedLanguage,
  nativeLanguageName,
  setLanguageLocales,
  SupportedLanguage,
} from "../i18n";
import axios from "../lib/axiosConfig";
import fetcher from "../lib/fetcher";
import { devLog } from "../utils/libs";

/**
 * Get tenant's default language from localStorage for initial render
 */
const getInitialTenantLanguage = (): SupportedLanguage => {
  if (typeof window !== 'undefined') {
    try {
      const storedTenant = localStorage.getItem('PD-tenant');
      if (storedTenant) {
        const parsedTenant = JSON.parse(storedTenant);
        const tenantLang = parsedTenant?.default_language as SupportedLanguage | undefined;
        if (isSupportedLanguage(tenantLang)) {
          return tenantLang;
        }
      }
    } catch (error) {
      devLog('Failed to read tenant language from localStorage', error);
    }
  }
  return DEFAULT_LANGUAGE;
};

interface LanguageContextType {
  // Existing MED terminology customization
  medSingular: string;
  medPlural: string;
  setMedSingular: (singular: string) => void;
  setMedPlural: (plural: string) => void;

  // New i18n functionality
  currentLanguage: SupportedLanguage;
  /** Code to native name, e.g. { en: "English", fr: "Français" } */
  supportedLanguages: Record<SupportedLanguage, string>;
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  isChangingLanguage: boolean;

  // User and tenant language preferences
  userLanguage: SupportedLanguage | null;
  tenantDefaultLanguage: SupportedLanguage;
  effectiveLanguage: SupportedLanguage;
}

const LanguageContext = createContext<LanguageContextType>({
  medSingular: "CRE",
  medPlural: "CREs",
  setMedSingular: () => { },
  setMedPlural: () => { },
  currentLanguage: DEFAULT_LANGUAGE,
  supportedLanguages: Object.fromEntries(AVAILABLE_LANGUAGES.map((code) => [code, nativeLanguageName(code)])),
  setLanguage: async () => { },
  isChangingLanguage: false,
  userLanguage: null,
  tenantDefaultLanguage: DEFAULT_LANGUAGE,
  effectiveLanguage: DEFAULT_LANGUAGE,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n: i18nInstance } = useTranslation();

  // Existing MED terminology state
  const [medSingular, setMedSingular] = useState("CRE");
  const [medPlural, setMedPlural] = useState("CREs");

  // Language state - API is source of truth
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(
    (i18nInstance.language as SupportedLanguage) || DEFAULT_LANGUAGE
  );
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [userLanguage, setUserLanguage] = useState<SupportedLanguage | null>(null);
  const [tenantDefaultLanguage, setTenantDefaultLanguage] = useState<SupportedLanguage>(getInitialTenantLanguage());
  const [effectiveLanguage, setEffectiveLanguage] = useState<SupportedLanguage>(getInitialTenantLanguage());
  const hasFetchedSettings = useRef(false);

  // The backend owns the language list. Offer the ones we also have strings for;
  // until it answers (or if it fails), offer every locale file we ship.
  const { data: supported } = useSWR<{ languages: Record<string, string>; locales?: Record<string, string> }>(
    "/api/v1/languages/supported",
    fetcher,
    { revalidateOnFocus: false }
  );
  useEffect(() => {
    if (supported?.locales) setLanguageLocales(supported.locales);
  }, [supported]);
  const supportedLanguages = useMemo(() => {
    if (!supported?.languages) {
      return Object.fromEntries(AVAILABLE_LANGUAGES.map((code) => [code, nativeLanguageName(code)]));
    }
    const missing = Object.keys(supported.languages).filter((code) => !isSupportedLanguage(code));
    if (process.env.NODE_ENV !== "production" && missing.length) {
      console.warn(`No src/i18n/locales file for supported language(s): ${missing.join(", ")}`);
    }
    return Object.fromEntries(Object.entries(supported.languages).filter(([code]) => isSupportedLanguage(code)));
  }, [supported]);

  /**
   * Fetch the user's language settings from the API
   */
  const fetchLanguageSettings = useCallback(async () => {
    if (hasFetchedSettings.current) {
      return;
    }
    hasFetchedSettings.current = true;

    try {
      const response = await axios.get("/api/v1/languages/user");
      const { user_language, tenant_default_language, effective_language } = response.data;

      setUserLanguage(user_language as SupportedLanguage | null);
      setTenantDefaultLanguage((tenant_default_language || DEFAULT_LANGUAGE) as SupportedLanguage);
      setEffectiveLanguage((effective_language || DEFAULT_LANGUAGE) as SupportedLanguage);

      // Update i18n if effective language differs
      if (effective_language && effective_language !== i18nInstance.language) {
        await ensureLanguageResources(effective_language);
        await i18nInstance.changeLanguage(effective_language);
        setCurrentLanguage(effective_language as SupportedLanguage);
      }

      devLog("Language settings loaded:", { user_language, tenant_default_language, effective_language });
    } catch (error) {
      devLog("Failed to fetch language settings:", error);
      hasFetchedSettings.current = false; // Allow retry on error
    }
  }, [i18nInstance]);

  /**
   * Change the user's language preference
   */
  const setLanguage = useCallback(async (language: SupportedLanguage) => {
    console.log("Setting language to:", language);
    setIsChangingLanguage(true);

    try {
      // Update i18n immediately for responsive UX
      await ensureLanguageResources(language);
      await i18nInstance.changeLanguage(language);
      setCurrentLanguage(language);

      // Persist to backend (API is source of truth)
      const response = await axios.put("/api/v1/languages/user", {
        language: language,
      });

      const { effective_language } = response.data;
      setUserLanguage(language);
      setEffectiveLanguage(effective_language as SupportedLanguage);

      devLog("Language changed to:", language);
    } catch (error) {
      devLog("Failed to update language preference:", error);
      // Revert on failure
      await i18nInstance.changeLanguage(effectiveLanguage);
      setCurrentLanguage(effectiveLanguage);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [i18nInstance, effectiveLanguage]);

  // Listen for i18n language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng as SupportedLanguage);
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  // Fetch language settings when component mounts
  // Note: This will be called after auth is established
  useEffect(() => {
    // Only fetch if user is logged in (check for tokens in localStorage)
    const hasAuthToken = localStorage.getItem("pd-tokens");
    if (hasAuthToken) {
      fetchLanguageSettings();
    }
  }, [fetchLanguageSettings]);

  return (
    <LanguageContext.Provider
      value={{
        medSingular,
        medPlural,
        setMedSingular,
        setMedPlural,
        currentLanguage,
        supportedLanguages,
        setLanguage,
        isChangingLanguage,
        userLanguage,
        tenantDefaultLanguage,
        effectiveLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
export const useLanguage = () => useContext(LanguageContext);
